import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  Link2,
  Shield,
  ArrowLeft,
  LogOut,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

const AdminSidebar = ({ open, onClose }: AdminSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const navItems: NavItem[] = [
    { label: t('admin.dashboard'), href: '/admin', icon: <LayoutDashboard className="w-5 h-5" /> },
    { label: t('admin.users'), href: '/admin/users', icon: <Users className="w-5 h-5" /> },
    { label: t('admin.deeplinks'), href: '/admin/deeplinks', icon: <Link2 className="w-5 h-5" /> },
    { label: t('admin.whitelist'), href: '/admin/whitelist', icon: <Shield className="w-5 h-5" /> },
  ];

  const handleLogout = async () => {
    await logout();
    onClose();
    navigate('/login');
  };

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href;

    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200
          ${isActive
            ? 'bg-red-500 text-white font-medium shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-dark-800 dark:hover:text-white'
          }
        `}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Overlay (mobile only) */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 md:top-16 bottom-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 overflow-y-auto z-50 md:z-30
          transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        <div className="flex flex-col h-full p-4">
          {/* Mobile close button */}
          <div className="flex items-center justify-between mb-4 md:hidden">
            <Link to="/admin" className="flex items-center gap-2" onClick={onClose}>
              <div className="w-8 h-8 bg-red-500 rounded-xl flex items-center justify-center">
                <span className="font-bold text-white">K</span>
              </div>
              <span className="font-bold text-xl text-dark-800 dark:text-white">Admin</span>
            </Link>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-dark-800 dark:hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1">
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>

            <hr className="my-6 border-gray-100 dark:border-gray-700" />

            {/* Back to app */}
            <Link
              to="/dashboard"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-dark-800 dark:hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('admin.backToApp')}</span>
            </Link>

            <hr className="my-6 border-gray-100 dark:border-gray-700" />

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 w-full"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('nav.logOut')}</span>
            </button>
          </nav>

          {/* Admin badge */}
          <div className="mt-auto">
            <div className="bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-500/20 dark:to-orange-500/20 dark:border dark:border-red-500/30 rounded-3xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 bg-red-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xs font-bold">A</span>
                </div>
                <h4 className="font-semibold text-dark-800 dark:text-white">{t('admin.adminPanel')}</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('admin.adminPanelDescription')}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
